import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CardCarousel = ({ 
  children, 
  title, 
  subtitle,
  showDots = true,
  showArrows = true,
  autoplay = false,
  autoplayDelay = 4000,
  className = ''
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
    slidesToScroll: 1,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Autoplay
  useEffect(() => {
    if (!emblaApi || !autoplay) return;
    const interval = setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollTo(0);
      }
    }, autoplayDelay);
    return () => clearInterval(interval);
  }, [emblaApi, autoplay, autoplayDelay]);

  const childrenArray = React.Children.toArray(children);

  return (
    <div className={`relative ${className}`}>
      {/* Header */}
      {(title || subtitle) && (
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            {subtitle && (
              <p className="text-fgs-primary text-sm font-medium uppercase tracking-wider mb-2">{subtitle}</p>
            )}
            {title && (
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">{title}</h2>
            )}
          </div>
          
          {/* Desktop Arrows */}
          {showArrows && (
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={scrollPrev}
                disabled={!canScrollPrev}
                className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="Précédent"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={scrollNext}
                disabled={!canScrollNext}
                className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="Suivant"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Carousel */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {childrenArray.map((child, index) => (
            <div 
              key={index} 
              className="flex-shrink-0 min-w-0 w-[80%] sm:w-[45%] md:w-[30%] lg:w-[23%]"
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Dots Navigation */}
      {showDots && scrollSnaps.length > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === selectedIndex 
                  ? 'bg-fgs-primary w-6' 
                  : 'bg-fgs-border hover:bg-fgs-text-muted'
              }`}
              aria-label={`Aller à la slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Mobile swipe hint */}
      <div className="md:hidden flex items-center justify-center gap-2 mt-4 text-fgs-text-muted text-xs">
        <ChevronLeft className="w-3 h-3" />
        <span>Glissez pour voir plus</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  );
};

// Card component for consistent styling
export const CarouselCard = ({ icon: Icon, title, description, highlight = false, color }) => (
  <div className={`h-full p-5 md:p-6 rounded-xl border transition-all ${
    highlight 
      ? 'bg-gradient-to-br from-fgs-primary/20 to-fgs-primary/5 border-fgs-primary/30' 
      : 'bg-fgs-surface border-fgs-border hover:border-fgs-primary/30'
  }`}>
    <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-4 ${
      color || 'bg-gradient-to-br from-fgs-primary/20 to-fgs-primary/10'
    }`}>
      <Icon className={`w-6 h-6 md:w-7 md:h-7 ${highlight ? 'text-fgs-primary' : 'text-white'}`} />
    </div>
    <h3 className="font-semibold text-white text-base md:text-lg mb-2">{title}</h3>
    <p className="text-fgs-text-muted text-sm leading-relaxed">{description}</p>
  </div>
);

export default CardCarousel;
